import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';

const values = [
  { icon: 'ri-shield-check-line', title: 'Integrity', desc: 'We operate with full transparency and honesty in every client engagement.' },
  { icon: 'ri-lightbulb-line', title: 'Expertise', desc: 'ACHC-certified knowledge backed by real-world home health industry experience.' },
  { icon: 'ri-focus-3-line', title: 'Results-Driven', desc: 'Every strategy we implement is designed to deliver measurable, lasting outcomes.' },
  { icon: 'ri-team-line', title: 'Partnership', desc: 'We work alongside your team as a true partner, not just an outside consultant.' },
];

const milestones = [
  { year: '2014', event: 'Founded with a mission to simplify ACHC accreditation for home health agencies.' },
  { year: '2016', event: 'Achieved ACHC Certified Consultant designation — one of the first in the nation.' },
  { year: '2019', event: 'Expanded services to include full compliance system setup and staff training programs.' },
  { year: '2022', event: 'Surpassed 50 agencies successfully accredited with a 100% pass rate.' },
  { year: '2024', event: 'Launched ongoing compliance support retainer program for sustained agency excellence.' },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about#webpage`,
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
  name: 'About Advantixx | ACHC Home Health Accreditation Consultants',
  description: 'Learn about Advantixx — a specialized ACHC accreditation consulting firm led by Dr. Osmel Villarreal, DBA. Our mission is to simplify compliance and strengthen home health agency operations.',
  isPartOf: {
    '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`,
  },
  about: {
    '@type': 'Organization',
    name: 'Advantixx Home Health Accreditation Consultants',
    foundingDate: '2014',
    description: 'Specialized consulting firm dedicated to helping home health agencies achieve and maintain ACHC accreditation.',
    founder: {
      '@type': 'Person',
      name: 'Dr. Osmel Villarreal',
      jobTitle: 'ACHC Certified Consultant, DBA, MBA, MS',
    },
  },
};

export default function AboutPage() {
  useSEO({
    title: 'About Advantixx | ACHC Home Health Accreditation Consultants',
    description: 'Learn about Advantixx — a specialized ACHC accreditation consulting firm led by Dr. Osmel Villarreal, DBA. Our mission is to simplify compliance and strengthen home health agency operations.',
    keywords: 'about Advantixx, ACHC consulting firm, Dr. Osmel Villarreal, home health accreditation experts',
    canonical: '/about',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-about');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/bcc85cc48cf591bf899b50636accd695.jpg"
            alt="About Advantixx"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/85"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#d4af37]"></div>
            <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Our Story</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6">About Advantixx</h1>
          <p className="text-[#b0b0b0] text-lg max-w-2xl leading-relaxed">
            A specialized consulting firm dedicated to helping home health agencies successfully achieve and maintain ACHC accreditation.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-[#111111] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px bg-[#d4af37]"></div>
                <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Our Mission</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-6 leading-tight">
                Simplifying Compliance,
                <br />
                <span className="text-[#d4af37]">Strengthening Operations</span>
              </h2>
              <p className="text-[#999] text-base leading-relaxed mb-5">
                Advantixx Home Health Accreditation Consultants is a specialized consulting firm dedicated to helping home health agencies successfully achieve and maintain ACHC accreditation.
              </p>
              <p className="text-[#999] text-base leading-relaxed mb-5">
                Led by an ACHC Certified Consultant, Advantixx provides expert guidance through every phase of the accreditation process. From initial readiness to final survey preparation, we ensure agencies meet all regulatory requirements efficiently and effectively.
              </p>
              <p className="text-[#999] text-base leading-relaxed mb-8">
                We understand that accreditation is more than a requirement — it is a foundation for delivering high-quality care and building a reputable healthcare organization. Our mission is to simplify compliance, strengthen operations, and position our clients for long-term success.
              </p>
              <Link
                to="/services"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 cursor-pointer"
              >
                Explore Our Services
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20healthcare%20consultant%20team%20in%20elegant%20dark%20office%20reviewing%20ACHC%20accreditation%20documents%2C%20warm%20golden%20lighting%2C%20sophisticated%20business%20environment%2C%20diverse%20team%20of%20professionals%20collaborating%2C%20premium%20corporate%20setting%20with%20gold%20accents&width=700&height=500&seq=about002&orientation=landscape"
                alt="Advantixx team"
                className="w-full h-80 md:h-96 object-cover object-top rounded-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#d4af37] text-[#0d0d0d] rounded-xl p-5 shadow-xl">
                <p className="text-3xl font-black">100%</p>
                <p className="text-xs font-bold uppercase tracking-wider mt-1">ACHC Pass Rate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#0d0d0d] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#d4af37]"></div>
              <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Core Values</span>
              <div className="w-8 h-px bg-[#d4af37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-[#161616] border border-[#d4af37]/10 rounded-xl p-7 hover:border-[#d4af37]/40 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 mb-5">
                  <i className={`${v.icon} text-[#d4af37] text-xl`}></i>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{v.title}</h3>
                <p className="text-[#888] text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-[#111111] py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#d4af37]"></div>
              <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Our Journey</span>
              <div className="w-8 h-px bg-[#d4af37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white">A Decade of Excellence</h2>
          </div>
          <div className="relative">
            <div className="absolute left-[88px] top-0 bottom-0 w-px bg-[#d4af37]/20 hidden sm:block"></div>
            <div className="flex flex-col gap-8">
              {milestones.map((m) => (
                <div key={m.year} className="flex items-start gap-6 sm:gap-10">
                  <div className="shrink-0 w-16 text-right">
                    <span className="text-[#d4af37] font-black text-lg">{m.year}</span>
                  </div>
                  <div className="relative shrink-0 hidden sm:flex items-center justify-center">
                    <div className="w-4 h-4 rounded-full bg-[#d4af37] border-2 border-[#111111] z-10"></div>
                  </div>
                  <div className="bg-[#161616] border border-[#d4af37]/10 rounded-xl p-5 flex-1">
                    <p className="text-[#ccc] text-sm leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0d0d0d] py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Ready to Work With <span className="text-[#d4af37]">Advantixx?</span>
          </h2>
          <p className="text-[#888] text-base mb-8 leading-relaxed">
            Whether you&apos;re launching a new agency or seeking to maintain compliance, we&apos;re here to guide you every step of the way.
          </p>
          <Link
            to="/services"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-10 py-4 rounded-full hover:bg-[#f4c430] transition-colors duration-200 cursor-pointer"
          >
            Get Started Today
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
